import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, ArrowRight, UserPlus, Users as MeetingIcon, AlertTriangle } from 'lucide-react';
import { Publication } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { coauthorAPI } from '../../services/api';
import MeetingModal from '../Meetings/MeetingModal';
import toast from 'react-hot-toast';

interface PublicationListItemProps {
  publication: Publication;
}

const PublicationListItem: React.FC<PublicationListItemProps> = ({ publication }) => {
  const { user } = useAuth();
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case '1': return 'bg-blue-100 text-blue-800';
      case '2': return 'bg-yellow-100 text-yellow-800';
      case '3': return 'bg-orange-100 text-orange-800';
      case '4': return 'bg-purple-100 text-purple-800';
      case 'published': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageText = (stage: string) => {
    switch (stage) {
      case '1': return 'Ideation';
      case '2': return 'Review & Team';
      case '3': return 'Data Analysis';
      case '4': return 'Drafting Manuscript';
      case 'published': return 'Published';
      default: return stage;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved - Recruiting Co-authors': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Under Review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const canJoinAsCoauthor = user && 
    user.id !== (publication.leadAuthorId || publication.lead_author_id) && 
    (publication.stage === '2' || publication.stage === '3') &&
    publication.status !== 'Rejected';

  const canCreateMeeting = user && 
    user.id === (publication.leadAuthorId || publication.lead_author_id) && 
    (publication.stage === '2' || publication.stage === '3' || publication.stage === '4') &&
    publication.status !== 'Rejected';

  const handleJoinAsCoauthor = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please log in to join as co-author');
      return;
    }

    try {
      await coauthorAPI.requestCoauthor(publication.id, {
        message: `I would like to join this publication as a co-author.`
      });
      toast.success('Co-author request submitted successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit co-author request');
    }
  };

  return (
    <>
      <Link to={`/publications/${publication.id}`} className="block">
      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all duration-200 group cursor-pointer">
        <div className="flex items-center justify-between">
          {/* Title and Stage */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-green-700 transition-colors">
                {publication.title}
              </h3>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStageColor(publication.stage)}`}>
                {getStageText(publication.stage)}
              </span>
              {(publication.priorityLevel === 'priority' || publication.priority_level === 'priority') && (
                <div className="flex items-center space-x-1">
                  <AlertTriangle className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600 font-medium">Priority</span>
                </div>
              )}
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(publication.status)}`}>
                {publication.status}
              </span>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex items-center space-x-6 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3" />
              <span>
                {formatDate(
                  publication.submittedAt || publication.submitted_at || ''
                )}
              </span>
            </div>
            
            <div className="flex items-center space-x-1">
              <Users className="h-3 w-3" />
              <span>{publication.authorCount || publication.author_count || 1} author{(publication.authorCount || publication.author_count || 1) > 1 ? 's' : ''}</span>
            </div>

            <div className="text-xs text-gray-500">
              Lead: {publication.leadAuthorName || publication.lead_author_name || 'Unknown Author'}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-1">
              {canCreateMeeting && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsMeetingModalOpen(true);
                  }}
                  className="flex items-center space-x-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                >
                  <MeetingIcon className="h-3 w-3" />
                  <span>Meeting</span>
                </button>
              )}
              
              {canJoinAsCoauthor && (
                <button
                  onClick={handleJoinAsCoauthor}
                  className="flex items-center space-x-1 text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
                >
                  <UserPlus className="h-3 w-3" />
                  <span>Join</span>
                </button>
              )}
            </div>

            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 transition-colors" />
          </div>
        </div>
      </div>
    </Link>
    
    {/* Meeting Modal */}
    <MeetingModal
      isOpen={isMeetingModalOpen}
      onClose={() => setIsMeetingModalOpen(false)}
      publicationId={publication.id}
      publicationTitle={publication.title}
      leadAuthorId={publication.lead_author_id}
      onMeetingCreated={() => {
        // Refresh any relevant data if needed
        console.log('Meeting created successfully');
      }}
    />
    </>
  );
};

export default PublicationListItem;
